import type {
  EnrollmentMonitoringMapDto,
  FunnelSectionDto,
  FunnelStageMetricDto,
  MetricValueDtoLong,
  MonitoredCriticalElementDto,
  PercentMetricValueDto,
} from 'api/generated/schemas';

import type {
  CriticalElementMonitoringMap,
  CriticalElementSummary,
  ExtendWithOption,
  ExtensionMetric,
  FunnelEntryDataStatus,
  FunnelSection,
  FunnelStageKey,
  FunnelStageMetric,
  MetricValue,
  PercentMetricValue,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';
import { formatMonitoringPercent } from 'containers/user/critical-elements-monitoring/helpers/format-monitoring-value';

const FUNNEL_STAGE_KEYS: readonly FunnelStageKey[] = [
  'inquiry',
  'application',
  'admit',
  'netDeposit',
];

const isFunnelStageKey = (
  key: FunnelStageMetricDto['key']
): key is FunnelStageKey =>
  !!key && (FUNNEL_STAGE_KEYS as readonly string[]).includes(key);

const isExtendWithOption = (
  key: FunnelStageMetricDto['key']
): key is ExtendWithOption =>
  key === 'prospect' || key === 'financial-aid' || key === 'retention';

const EXTENSION_LABEL: Record<ExtendWithOption, string> = {
  prospect: 'Prospects',
  'financial-aid': 'Filers',
  retention: 'Retention',
};

const EXTENSION_SECONDARY_CAPTION_PREFIX: Partial<
  Record<ExtendWithOption, string>
> = {
  retention: 'Historical persistence',
};

export const toCriticalElementSummary = (
  dto: MonitoredCriticalElementDto
): CriticalElementSummary => ({
  id: dto.id ?? '',
  name: dto.name ?? '',
  dimensionMeta: dto.dimensionMeta ?? '',
});

const toMetricValue = (dto: MetricValueDtoLong | undefined): MetricValue => ({
  current: dto?.current ?? null,
  previous: dto?.previous ?? null,
  deltaPct: dto?.deltaPct ?? null,
});

const toPercentMetricValue = (
  dto: PercentMetricValueDto | undefined
): PercentMetricValue => ({
  current: dto?.current ?? null,
  previous: dto?.previous ?? null,
  deltaPct: dto?.deltaPct ?? null,
  pp: dto?.pp ?? null,
});

const toDataStatus = (
  dto: FunnelStageMetricDto
): FunnelEntryDataStatus | undefined =>
  dto.volume?.current == null ? 'not-recorded' : undefined;

const toFunnelStageMetric = (
  dto: FunnelStageMetricDto & { key: FunnelStageKey }
): FunnelStageMetric => ({
  stageKey: dto.key,
  volume: toMetricValue(dto.volume),
  rate: toPercentMetricValue(dto.rate),
  proportion: toPercentMetricValue(dto.proportion),
  comparators: {
    client: dto.comparators?.client,
    cohort: dto.comparators?.cohort,
    vsCohort: dto.comparators?.vsCohort,
    clientPp: dto.comparators?.clientPp,
    cohortPp: dto.comparators?.cohortPp,
    vsCohortPp: dto.comparators?.vsCohortPp,
    clientProportion: dto.comparators?.clientProportion,
    cohortProportion: dto.comparators?.cohortProportion,
    vsCohortProportion: dto.comparators?.vsCohortProportion,
    cohortRateCurrent: dto.comparators?.cohortRateCurrent,
    cohortProportionCurrent: dto.comparators?.cohortProportionCurrent,
  },
  dataStatus: toDataStatus(dto),
});

const toExtensionMetric = (
  dto: FunnelStageMetricDto & { key: ExtendWithOption }
): ExtensionMetric => {
  const rate = toPercentMetricValue(dto.rate);
  const captionPrefix = EXTENSION_SECONDARY_CAPTION_PREFIX[dto.key];
  const secondaryCaption =
    captionPrefix && rate.current !== null
      ? `${captionPrefix}: ${formatMonitoringPercent(rate.current)}`
      : '';

  return {
    type: dto.key,
    label: EXTENSION_LABEL[dto.key],
    secondaryCaption,
    volume: toMetricValue(dto.volume),
    rate,
    proportion: toPercentMetricValue(dto.proportion),
    comparators: {
      client: dto.comparators?.client,
      cohort: dto.comparators?.cohort,
      vsCohort: dto.comparators?.vsCohort,
      clientPp: dto.comparators?.clientPp,
      cohortPp: dto.comparators?.cohortPp,
      vsCohortPp: dto.comparators?.vsCohortPp,
      clientProportion: dto.comparators?.clientProportion,
      cohortProportion: dto.comparators?.cohortProportion,
      vsCohortProportion: dto.comparators?.vsCohortProportion,
      cohortRateCurrent: dto.comparators?.cohortRateCurrent,
      cohortProportionCurrent: dto.comparators?.cohortProportionCurrent,
    },
    dataStatus: toDataStatus(dto),
  };
};

const toFunnelSection = (dto: FunnelSectionDto | undefined): FunnelSection => {
  const entries = dto?.stages ?? [];

  return {
    period: {
      monitoringYear: dto?.period?.monitoringYear ?? 0,
      enteringClass: dto?.period?.enteringClass ?? 0,
    },
    cohortLabel: dto?.cohortLabel ?? '',
    stages: entries
      .filter(
        (entry): entry is FunnelStageMetricDto & { key: FunnelStageKey } =>
          isFunnelStageKey(entry.key)
      )
      .map(toFunnelStageMetric),
    extensions: entries
      .filter(
        (entry): entry is FunnelStageMetricDto & { key: ExtendWithOption } =>
          isExtendWithOption(entry.key)
      )
      .map(toExtensionMetric),
  };
};

export const toCriticalElementMonitoringMap = (
  dto: EnrollmentMonitoringMapDto
): CriticalElementMonitoringMap => ({
  total: toFunnelSection(dto.total),
  byCriticalElementId: Object.fromEntries(
    Object.entries(dto.byCriticalElementId ?? {}).map(([id, section]) => [
      id,
      toFunnelSection(section),
    ])
  ),
});
